/** Resolution of the user's saved navigation layout against the entries the shell can actually show.
 *
 *  The daemon stores menu and pin id lists (see `packages/db/src/config/codecs/store/navSettings.ts`).
 *  All the meaning lives here: which entries are hidden, in what order the rest appears, and what a single
 *  "move up" or "hide" does to the stored lists.
 *
 *  Two rules shape everything below:
 *  - An id that matches no current entry is CARRIED, never dropped. A plugin world vanishes from the
 *    listing while its plugin is disabled, and forgetting its position would silently reshuffle the menu
 *    every time a plugin was toggled.
 *  - A new entry (a freshly enabled plugin) appears at the END of the menu rather than at a position
 *    nobody chose for it. */

import type { NavLayout } from './types';

export const EMPTY_NAV_LAYOUT: NavLayout = { hidden: [], order: [], pinOrder: [] };

/** Read a layout that came from outside the app (the browser's own cache of the last known one). Anything
 *  that is not three lists of ids is rejected outright rather than partially trusted — the menu falls back
 *  to registry order, which is always safe. */
export function parseNavLayout(raw: unknown): NavLayout | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const { hidden, order, pinOrder } = raw as { hidden?: unknown; order?: unknown; pinOrder?: unknown };
  const ids = (value: unknown): string[] | null =>
    Array.isArray(value) && value.every((id) => typeof id === 'string') ? (value as string[]) : null;
  const hiddenIds = ids(hidden);
  const orderIds = ids(order);
  const pinIds = ids(pinOrder);
  const width = (raw as { sidebarWidth?: unknown }).sidebarWidth;
  if (width !== undefined && width !== null && (typeof width !== 'number' || !Number.isInteger(width) || width < 240 || width > 480)) return null;
  return hiddenIds && orderIds && pinIds
    ? { hidden: hiddenIds, order: orderIds, pinOrder: pinIds, ...(width === undefined ? {} : { sidebarWidth: width }) } : null;
}

/** Order entries by the saved arrangement and drop the hidden ones. Entries the layout says nothing about
 *  keep their registry order, after everything it does place. */
export function applyNavLayout<T extends { id?: string }>(entries: T[], layout: NavLayout): T[] {
  const hidden = new Set(layout.hidden);
  const rank = new Map(layout.order.map((id, index) => [id, index] as const));
  // An entry without an id cannot be addressed by the layout, so it can never be hidden or ranked and
  // simply keeps its registry position.
  return entries
    .filter((entry) => !(entry.id && hidden.has(entry.id)))
    .map((entry, index) => ({ entry, index }))
    .sort((a, b) => {
      const rankA = a.entry.id === undefined ? undefined : rank.get(a.entry.id);
      const rankB = b.entry.id === undefined ? undefined : rank.get(b.entry.id);
      if (rankA !== undefined && rankB !== undefined) return rankA - rankB;
      if (rankA !== undefined) return -1;
      if (rankB !== undefined) return 1;
      return a.index - b.index;
    })
    .map(({ entry }) => entry);
}

/** The entries the user hid, in menu order — what the "show hidden" modal lists and counts. */
export function hiddenNavEntries<T extends { id?: string }>(entries: T[], layout: NavLayout): T[] {
  const hidden = new Set(layout.hidden);
  return entries.filter((entry) => !!entry.id && hidden.has(entry.id));
}

/** Bring the stored order up to date with the entries that exist now: unknown ids keep their position,
 *  entries the order has never seen are appended. Editing operations run against this form, so both the
 *  moved entry and its neighbour are guaranteed to be present. */
/** @internal web/tests/lib/navLayout.test.ts checks preserved and appended ids. */
export function normalizeNavOrder(order: string[], entryIds: string[]): string[] {
  const placed = new Set(order);
  return [...order, ...entryIds.filter((id) => !placed.has(id))];
}

/** Drop one entry into the seat `targetId` occupies in the VISIBLE menu — used by drag and keyboard menu
 *  actions. The target is addressed by id, never by an index: a surface partitions the menu into its own
 *  groups and sequence, and an index read there names a different seat in the stored order. It only
 *  rewrites the slots visible entries already occupy, so hidden entries keep their place and come back
 *  where the user left them. */
export function reorderNavEntry(layout: NavLayout, entryIds: string[], id: string, targetId: string): NavLayout {
  const order = normalizeNavOrder(layout.order, entryIds);
  const hidden = new Set(layout.hidden);
  const isVisible = (entryId: string) => !hidden.has(entryId) && entryIds.includes(entryId);
  const visible = order.filter(isVisible);

  const from = visible.indexOf(id);
  const to = visible.indexOf(targetId);
  if (from < 0 || to < 0) throw new Error('Cannot reorder a navigation entry that is not on show');
  if (from === to) return { ...layout, order };

  const resequenced = [...visible];
  resequenced.splice(from, 1);
  resequenced.splice(to, 0, id);

  const next = [...order];
  let slot = 0;
  for (let index = 0; index < next.length; index++) {
    if (isVisible(next[index])) next[index] = resequenced[slot++];
  }
  return { ...layout, order: next };
}

/** Move among the currently pinned ids only; stale stored ids are discarded on an edit. */
export function reorderPinnedConversation(layout: NavLayout, pinnedIds: readonly string[], id: string, toIndex: number): NavLayout {
  const pinOrder = [...pinnedIds];
  const from = pinOrder.indexOf(id);
  if (from < 0) throw new Error('Cannot reorder a conversation that is not pinned');
  pinOrder.splice(from, 1);
  pinOrder.splice(Math.max(0, Math.min(pinOrder.length, toIndex)), 0, id);
  return { ...layout, pinOrder };
}

/** Hide or show one entry. Hiding records the id; showing removes it.
 *
 *  The order is deliberately left ALONE. Hiding says nothing about arrangement, and writing an order out
 *  of a hide would commit the menu to one — which on a surface that sorts by its own rule (the rail's
 *  spatial axis) re-sorts every remaining entry the first time anything is hidden. An untouched order
 *  keeps every surface free to arrange itself as it always did. */
export function setNavEntryHidden(layout: NavLayout, id: string, hidden: boolean): NavLayout {
  const next = layout.hidden.filter((entryId) => entryId !== id);
  return { ...layout, hidden: hidden ? [...next, id] : next };
}
